import React from 'react';
import { Tag, Avatar, Typography, Space, List, Drawer } from 'antd';
import { BugOutlined, ToolOutlined, BulbOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { getRouteLabel } from './routeLabels';

dayjs.extend(relativeTime);
dayjs.locale('es');

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Tipo metadata — maps DB enum values to display properties
// ---------------------------------------------------------------------------
export const TIPO_META = {
  falla: { label: 'Bug', color: 'red', icon: <BugOutlined /> },
  mejora: { label: 'Mejora', color: 'blue', icon: <ToolOutlined /> },
  nueva_funcion: { label: 'Nueva función', color: 'green', icon: <BulbOutlined /> },
};

// ---------------------------------------------------------------------------
// Estado display maps — colours match Ant Design Tag `color` prop values
// Design §3.3: recibido→default, en_revision→gold, en_desarrollo→blue,
//              lanzado→success, descartado→error
// ---------------------------------------------------------------------------
export const ESTADO_COLORS = {
  recibido: 'default',
  en_revision: 'gold',
  en_desarrollo: 'blue',
  lanzado: 'success',
  descartado: 'error',
};

export const ESTADO_LABELS = {
  recibido: 'Recibido',
  en_revision: 'En revisión',
  en_desarrollo: 'En desarrollo',
  lanzado: 'Lanzado',
  descartado: 'Descartado',
};

// ---------------------------------------------------------------------------
// ReporteItem — single list entry used by both admin panel and MisReportes
//
// Props:
//   reporte       — document from the API
//   onEdit        — callback(reporte) for the admin edit button; omit for read-only views
//   showLanzadoTag — when true, render "✓ Esta mejora fue implementada" for lanzado docs
// ---------------------------------------------------------------------------
export function ReporteItem({ reporte, onEdit, onView, showLanzadoTag }) {
  const meta = TIPO_META[reporte.tipo] || {};

  // Build initials from the reporter's name (up to 2 characters)
  const reporterName = reporte.reportadoPor?.name ?? '';
  const initials = reporterName
    ? reporterName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '?';

  // Avatar background colour derived from tipo colour
  const avatarBg =
    meta.color === 'red'
      ? '#ff4d4f'
      : meta.color === 'blue'
      ? '#1677ff'
      : meta.color === 'green'
      ? '#52c41a'
      : '#8c8c8c';

  // Friendly module label (stored value may already be a label or a raw pathname)
  const moduloLabel = reporte.modulo
    ? getRouteLabel(reporte.modulo) !== 'App'
      ? getRouteLabel(reporte.modulo)
      : reporte.modulo
    : null;

  // Truncate long descriptions
  const shortDesc =
    reporte.descripcion && reporte.descripcion.length > 120
      ? reporte.descripcion.slice(0, 120) + '…'
      : reporte.descripcion;

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        padding: '12px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <Avatar style={{ backgroundColor: avatarBg, flexShrink: 0 }}>
        {initials}
      </Avatar>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Space size={4} wrap>
          <Tag color={meta.color} icon={meta.icon}>
            {meta.label}
          </Tag>
          <Tag color={ESTADO_COLORS[reporte.estado]}>
            {ESTADO_LABELS[reporte.estado] ?? reporte.estado}
          </Tag>
          {moduloLabel && <Tag>{moduloLabel}</Tag>}
        </Space>

        <Text
          style={{ display: 'block', marginTop: 4 }}
          ellipsis={{ tooltip: reporte.descripcion }}
        >
          {shortDesc}
        </Text>

        <Text type="secondary" style={{ fontSize: 12 }}>
          {reporterName && `${reporterName} · `}
          {dayjs(reporte.createdAt).fromNow()}
        </Text>

        {showLanzadoTag && reporte.estado === 'lanzado' && (
          <div style={{ marginTop: 4 }}>
            <Tag color="success">✓ Esta mejora fue implementada</Tag>
          </div>
        )}
      </div>

      {onView && (
        <button
          onClick={() => onView(reporte)}
          style={{
            background: 'none',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            padding: '4px 8px',
            cursor: 'pointer',
            color: '#595959',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <EyeOutlined />
          Ver detalle
        </button>
      )}

      {onEdit && (
        <button
          onClick={() => onEdit(reporte)}
          style={{
            background: 'none',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            padding: '4px 8px',
            cursor: 'pointer',
            color: '#595959',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <EditOutlined />
          Editar estado
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReporteList — wraps Ant Design List over an array of reports
//
// Props:
//   reportes       — array of report documents
//   onEdit         — callback(reporte) passed through to ReporteItem; omit for read-only
//   showLanzadoTag — passed through to ReporteItem
// ---------------------------------------------------------------------------
export function ReporteList({ reportes, onEdit, onView, showLanzadoTag }) {
  if (!reportes?.length) return null;

  return (
    <List
      dataSource={reportes}
      renderItem={(reporte) => (
        <List.Item style={{ padding: 0, border: 'none' }} key={reporte._id}>
          <ReporteItem
            reporte={reporte}
            onEdit={onEdit}
            onView={onView}
            showLanzadoTag={showLanzadoTag}
          />
        </List.Item>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// ReporteDetailDrawer — read-only side panel showing full report details
// ---------------------------------------------------------------------------
export function ReporteDetailDrawer({ reporte, open, onClose }) {
  if (!reporte) return null;
  const meta = TIPO_META[reporte.tipo] || {};
  const reporterName = reporte.reportadoPor?.name ?? '';
  const moduloLabel = reporte.modulo
    ? getRouteLabel(reporte.modulo) !== 'App'
      ? getRouteLabel(reporte.modulo)
      : reporte.modulo
    : '';

  return (
    <Drawer
      title="Detalle del reporte"
      width={480}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space size={4} wrap>
          <Tag color={meta.color} icon={meta.icon}>{meta.label}</Tag>
          <Tag color={ESTADO_COLORS[reporte.estado]}>
            {ESTADO_LABELS[reporte.estado] ?? reporte.estado}
          </Tag>
          {moduloLabel && <Tag>{moduloLabel}</Tag>}
        </Space>

        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {reporterName && `${reporterName} · `}
            {dayjs(reporte.createdAt).fromNow()}
          </Text>
        </div>

        <div>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>Descripción</div>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{reporte.descripcion}</Text>
        </div>

        {reporte.captura && (
          <div>
            <div style={{ fontWeight: 500, marginBottom: 8 }}>Adjunto</div>
            {reporte.captura.toLowerCase().endsWith('.pdf') ? (
              <a href={reporte.captura} target="_blank" rel="noopener noreferrer">
                Descargar PDF
              </a>
            ) : (
              <img
                src={reporte.captura}
                alt="captura"
                style={{ maxWidth: '100%', borderRadius: 4 }}
              />
            )}
          </div>
        )}

        {reporte.comentarios?.length > 0 && (
          <div>
            <div style={{ fontWeight: 500, marginBottom: 8 }}>Historial de comentarios</div>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              {[...reporte.comentarios]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((comentario) => (
                  <div
                    key={comentario._id ?? `${comentario.createdAt}-${comentario.texto}`}
                    style={{ borderLeft: '2px solid #f0f0f0', paddingLeft: 12 }}
                  >
                    <Space size={4} wrap>
                      <Tag color={ESTADO_COLORS[comentario.estado]}>
                        {ESTADO_LABELS[comentario.estado] ?? comentario.estado}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {comentario.autor?.name && `${comentario.autor.name} · `}
                        {dayjs(comentario.createdAt).fromNow()}
                      </Text>
                    </Space>
                    <Text style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                      {comentario.texto}
                    </Text>
                  </div>
                ))}
            </Space>
          </div>
        )}
      </Space>
    </Drawer>
  );
}
